import React from 'react';

import {
  Box,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';

import strings from '../../../localization';

import {
  ConversationMessage,
  sanitizeMessageForDisplay,
} from './conversation';
import styles from './styles';

interface IConversationMessages {
  copiedMessageId: string | null;
  emptyStateDescription: string;
  isClientToolProcessing: boolean;
  messages: ConversationMessage[];
  messagesEndRef: React.RefObject<HTMLDivElement>;
  onCopyMessage: (item: ConversationMessage) => void;
}

const ConversationMessages = ({
  copiedMessageId,
  emptyStateDescription,
  isClientToolProcessing,
  messages,
  messagesEndRef,
  onCopyMessage,
}: IConversationMessages) => {
  React.useEffect(() => {
    if (isClientToolProcessing) {
      messagesEndRef.current?.scrollIntoView?.({ behavior: 'smooth' });
    }
  }, [isClientToolProcessing, messagesEndRef]);

  return (
    <Box sx={styles.messages} aria-live="polite">
      {messages.length === 0 ? (
        <Box sx={styles.emptyState}>
          <Box sx={styles.emptyOrb}>
            <AutoAwesomeRoundedIcon sx={styles.emptyOrbIcon} />
          </Box>
          <Typography component="h2" sx={styles.emptyTitle}>
            {strings.startYourProfileConversation}
          </Typography>
          <Typography sx={styles.emptyDescription}>
            {emptyStateDescription}
          </Typography>
        </Box>
      ) : (
        messages.map((item) => {
          const visibleMessage = sanitizeMessageForDisplay(item.message);

          return (
            <Box
              key={item.id}
              sx={[
                styles.messageRow,
                item.role === 'user' ? styles.userMessageRow : {},
              ]}
            >
              {item.role === 'assistant' && (
                <Box sx={styles.messageAvatar}>
                  <AutoAwesomeRoundedIcon sx={styles.messageAvatarIcon} />
                </Box>
              )}
              <Box sx={styles.messageContent}>
                <Box
                  sx={[
                    styles.messageBubble,
                    item.role === 'user'
                      ? styles.userMessageBubble
                      : {},
                  ]}
                >
                  {item.isStreaming && !visibleMessage ? (
                    <Box sx={styles.typingIndicator}>
                      <Box component="span" />
                      <Box component="span" />
                      <Box component="span" />
                    </Box>
                  ) : (
                    <Typography sx={styles.messageText}>
                      {visibleMessage}
                    </Typography>
                  )}
                </Box>
                {item.role === 'assistant' && visibleMessage && (
                  <Tooltip
                    title={copiedMessageId === item.id
                      ? strings.copied
                      : strings.copyMessage}
                  >
                    <IconButton
                      aria-label={strings.copyMessage}
                      onClick={() =>
                        onCopyMessage(item)}
                      size="small"
                      sx={styles.copyButton}
                    >
                      {copiedMessageId === item.id
                        ? <CheckRoundedIcon fontSize="inherit" />
                        : <ContentCopyRoundedIcon fontSize="inherit" />}
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </Box>
          );
        })
      )}
      {isClientToolProcessing && (
        <Box role="status" sx={styles.messageRow}>
          <Box sx={styles.messageAvatar}>
            <AutoAwesomeRoundedIcon sx={styles.messageAvatarIcon} />
          </Box>
          <Box sx={styles.messageContent}>
            <Box sx={styles.messageBubble}>
              <Typography sx={styles.messageText}>
                {strings.aiCompanionProcessing}
              </Typography>
            </Box>
          </Box>
        </Box>
      )}
      <Box ref={messagesEndRef} />
    </Box>
  );
};

export default ConversationMessages;
